import { useState, useEffect, useRef } from 'react';
import { useTestStore, type TestId } from '@/store/useTestStore';
import { t } from '@/i18n/translations';
import { TestCard } from '@/components/TestCard';
import { MetricBox } from '@/components/MetricBox';

interface Props { autoRun: boolean; autoNonce: number; }

export function PwaTest({ autoRun, autoNonce }: Props) {
  const { language, reportResult, onAutoDone } = useTestStore();
  const [running, setRunning] = useState(false);
  const [metrics, setMetrics] = useState({
    serviceWorker: false,
    manifest: false,
    notifications: 'checking' as string,
    persistentStorage: 'checking' as string,
    installPrompt: false,
    cacheApi: false,
    indexedDB: false,
  });
  const autoDoneRef = useRef(false);

  function reset() {
    setRunning(false);
    autoDoneRef.current = false;
    setMetrics({
      serviceWorker: false, manifest: false,
      notifications: 'checking', persistentStorage: 'checking',
      installPrompt: false, cacheApi: false, indexedDB: false,
    });
  }

  async function start() {
    reset();
    setRunning(true);

    const hasSW = 'serviceWorker' in navigator;
    const hasCacheApi = 'caches' in window;
    const hasIDB = 'indexedDB' in window;
    const hasNotif = 'Notification' in window;
    const hasManifest = !!document.querySelector('link[rel="manifest"]');

    let notifStatus = 'no API';
    if (hasNotif) {
      notifStatus = Notification.permission === 'granted' ? 'granted'
        : Notification.permission === 'denied' ? 'denied' : 'available';
    }

    let storageStatus = 'no API';
    if (navigator.storage && typeof navigator.storage.persist === 'function') {
      try {
        const persisted = await navigator.storage.persisted();
        storageStatus = persisted ? 'persisted' : 'available';
      } catch { storageStatus = 'error'; }
    }

    setMetrics({
      serviceWorker: hasSW,
      manifest: hasManifest,
      notifications: notifStatus,
      persistentStorage: storageStatus,
      installPrompt: false, // We can't detect beforeinstallprompt reliably at test time
      cacheApi: hasCacheApi,
      indexedDB: hasIDB,
    });
  }

  function stop() {
    setRunning(false);
    let sc = 20;
    if (metrics.serviceWorker) sc += 20;
    if (metrics.manifest) sc += 10;
    if (metrics.notifications !== 'no API') sc += 10;
    if (metrics.persistentStorage !== 'no API') sc += 10;
    if (metrics.cacheApi) sc += 15;
    if (metrics.indexedDB) sc += 15;
    sc = Math.min(100, sc);

    const testId: TestId = 'mobile-pwa';
    reportResult(testId, {
      score: sc,
      primary: `SW: ${metrics.serviceWorker ? 'YES' : 'NO'} · Cache: ${metrics.cacheApi ? 'YES' : 'NO'}`,
      details: metrics,
    });
    if (autoRun && !autoDoneRef.current) { autoDoneRef.current = true; onAutoDone(testId); }
  }

  useEffect(() => {
    if (!autoRun) return;
    start();
    const timer = setTimeout(() => stop(), 2000);
    return () => clearTimeout(timer);
  }, [autoRun, autoNonce]);

  const statusIcon = (v: boolean | string) => {
    if (v === true) return t('supported.yes', language);
    if (v === false) return t('supported.no', language);
    return String(v);
  };

  return (
    <TestCard title={t('mobile.pwa.title', language)} description={t('mobile.pwa.desc', language)}>
      <div className="flex flex-wrap gap-2.5 items-center">
        <button className="btn btn-primary" onClick={start} disabled={running || autoRun}>{t('btn.start', language)}</button>
        <button className="btn btn-danger" onClick={stop} disabled={!running || autoRun}>{t('btn.stop', language)}</button>
        <button className="btn" onClick={reset} disabled={autoRun}>{t('btn.reset', language)}</button>
      </div>
      <div className="grid grid-cols-4 gap-2.5 mt-3 max-lg:grid-cols-2 max-sm:grid-cols-1">
        <MetricBox label={t('pwa.sw', language)} value={statusIcon(metrics.serviceWorker)} hint="Required for offline" />
        <MetricBox label={t('pwa.manifest', language)} value={statusIcon(metrics.manifest)} />
        <MetricBox label={t('pwa.notifications', language)} value={statusIcon(metrics.notifications)} />
        <MetricBox label={t('pwa.storage', language)} value={statusIcon(metrics.persistentStorage)} />
      </div>
      <div className="grid grid-cols-2 gap-2.5 mt-2.5 max-sm:grid-cols-1">
        <MetricBox label="Cache API" value={statusIcon(metrics.cacheApi)} />
        <MetricBox label="IndexedDB" value={statusIcon(metrics.indexedDB)} />
      </div>
    </TestCard>
  );
}
